"use client";

import * as React from "react";
import { Eye, Loader2, Plus, Sparkles, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";
import { formatHirePrice } from "~/lib/marketplace/hire-pricing";
import {
  AGENT_TRAINING_ADVANCED_MONTHLY_CENTS,
  AGENT_TRAINING_BASIC_MONTHLY_CENTS,
  type HireRecommendationsSelection,
} from "~/lib/marketplace/hire-recommendations-pricing";
import {
  defaultHireWizardTrainingDraft,
  type HireWizardTrainingDraft,
} from "~/lib/marketplace/hire-wizard-draft";
import { InfoTip } from "~/components/marketplace/hire-recommendations-step";

type HireWizardTrainingStepProps = {
  businessName: string;
  primaryWebsiteUrl: string;
  selection: HireRecommendationsSelection;
  onSelectionChange: (next: HireRecommendationsSelection) => void;
  trainingDraft: HireWizardTrainingDraft;
  onTrainingDraftChange: (next: HireWizardTrainingDraft) => void;
  onGenerateCompanyProfile?: () => Promise<void>;
  isGeneratingCompanyProfile?: boolean;
  onViewCompanyProfile?: () => void;
  onScrapeWebsitePages?: () => Promise<void>;
  isScrapingWebsitePages?: boolean;
  onGenerateFaqs?: () => Promise<void>;
  isGeneratingFaqs?: boolean;
  onUploadFiles?: (files: FileList) => Promise<void>;
  isUploadingFiles?: boolean;
  className?: string;
};

export function HireWizardTrainingStep({
  businessName,
  primaryWebsiteUrl,
  selection,
  onSelectionChange,
  trainingDraft,
  onTrainingDraftChange,
  onGenerateCompanyProfile,
  isGeneratingCompanyProfile = false,
  onViewCompanyProfile,
  onScrapeWebsitePages,
  isScrapingWebsitePages = false,
  onGenerateFaqs,
  isGeneratingFaqs = false,
  onUploadFiles,
  isUploadingFiles = false,
  className,
}: HireWizardTrainingStepProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const updateDraft = (patch: Partial<HireWizardTrainingDraft>) => {
    onTrainingDraftChange({ ...trainingDraft, ...patch });
  };

  const addWebsiteUrl = () => {
    updateDraft({
      additionalWebsiteUrls: [...trainingDraft.additionalWebsiteUrls, primaryWebsiteUrl || ""],
    });
  };

  const addTextSnippet = () => {
    updateDraft({ textSnippets: [...trainingDraft.textSnippets, ""] });
  };

  const addQaPair = () => {
    updateDraft({
      qaPairs: [...trainingDraft.qaPairs, { question: "", answer: "" }],
    });
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div>
        <h3 className="text-base font-semibold text-slate-900">Agent Training</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure how {businessName}&apos;s Inbound GPT Agent learns about your business.
        </p>
      </div>

      <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Basic training
              <InfoTip text="Core training from your website and AI-generated company profile." />
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {AGENT_TRAINING_BASIC_MONTHLY_CENTS === 0
                ? "Included free"
                : `${formatHirePrice(AGENT_TRAINING_BASIC_MONTHLY_CENTS)}/mo`}
            </p>
          </div>
          <Switch checked disabled aria-label="Basic training enabled" />
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900">Company Profile</p>
              <p className="text-xs text-slate-500">
                Generate an AI company profile from{" "}
                {primaryWebsiteUrl ? (
                  <span className="font-medium text-slate-700">{primaryWebsiteUrl}</span>
                ) : (
                  "your website"
                )}
                .
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isGeneratingCompanyProfile || !onGenerateCompanyProfile}
                onClick={() => void onGenerateCompanyProfile?.()}
              >
                {isGeneratingCompanyProfile ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate with AI
                  </>
                )}
              </Button>
              {trainingDraft.companyProfileGenerated && onViewCompanyProfile ? (
                <Button type="button" variant="secondary" size="sm" onClick={onViewCompanyProfile}>
                  <Eye className="mr-2 h-4 w-4" />
                  View profile
                </Button>
              ) : null}
            </div>
          </div>
          {trainingDraft.companyProfileGenerated ? (
            <p className="mt-2 text-xs font-medium text-emerald-700">
              Company profile generated from your website.
            </p>
          ) : null}
        </div>
      </div>

      <div className="space-y-4 rounded-lg border border-slate-200 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Advanced training
              <InfoTip text="Extended knowledge base with additional website pages, documents, custom text, and Q&A." />
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {formatHirePrice(AGENT_TRAINING_ADVANCED_MONTHLY_CENTS)}/mo
            </p>
          </div>
          <Switch
            checked={selection.agentTrainingAdvanced}
            onCheckedChange={(agentTrainingAdvanced) =>
              onSelectionChange({ ...selection, agentTrainingAdvanced })
            }
            aria-label="Enable advanced training"
          />
        </div>

        {selection.agentTrainingAdvanced ? (
          <div className="space-y-5 border-t border-slate-100 pt-4">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm font-medium">Website content</Label>
                <div className="flex flex-wrap gap-2">
                  {onScrapeWebsitePages ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isScrapingWebsitePages || !primaryWebsiteUrl}
                      onClick={() => void onScrapeWebsitePages()}
                    >
                      {isScrapingWebsitePages ? (
                        <>
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          Scraping…
                        </>
                      ) : (
                        "Scrape up to 10 pages"
                      )}
                    </Button>
                  ) : null}
                  <Button type="button" variant="ghost" size="sm" onClick={addWebsiteUrl}>
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add URL
                  </Button>
                </div>
              </div>
              {trainingDraft.scrapedPageUrls.length > 0 ? (
                <p className="text-xs text-emerald-700">
                  Scraped {trainingDraft.scrapedPageUrls.length} page
                  {trainingDraft.scrapedPageUrls.length === 1 ? "" : "s"} from your website.
                </p>
              ) : (
                <p className="text-xs text-slate-500">
                  Add pages from your site for the agent to learn from, or scrape up to 10 pages
                  automatically.
                </p>
              )}
              {trainingDraft.additionalWebsiteUrls.length === 0 ? null : (
                trainingDraft.additionalWebsiteUrls.map((url, index) => (
                  <div key={`url-${index}`} className="flex gap-2">
                    <Input
                      value={url}
                      placeholder="https://yourbusiness.com/services"
                      onChange={(e) => {
                        const next = [...trainingDraft.additionalWebsiteUrls];
                        next[index] = e.target.value;
                        updateDraft({ additionalWebsiteUrls: next });
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Remove URL"
                      onClick={() => {
                        const next = trainingDraft.additionalWebsiteUrls.filter(
                          (_, i) => i !== index,
                        );
                        updateDraft({ additionalWebsiteUrls: next });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">Files</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isUploadingFiles}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploadingFiles ? (
                    <>
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Upload
                    </>
                  )}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="sr-only"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.md"
                  onChange={(event) => {
                    const files = event.target.files;
                    if (files && files.length > 0 && onUploadFiles) {
                      void onUploadFiles(files);
                    }
                    event.target.value = "";
                  }}
                />
              </div>
              {trainingDraft.uploadedFiles.length > 0 ? (
                <ul className="space-y-1.5">
                  {trainingDraft.uploadedFiles.map((file) => (
                    <li
                      key={file.id}
                      className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs"
                    >
                      <span className="truncate font-medium text-slate-700">{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => {
                          updateDraft({
                            uploadedFiles: trainingDraft.uploadedFiles.filter(
                              (entry) => entry.id !== file.id,
                            ),
                          });
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">
                  Upload PDF, Word, or text files to include in your agent training.
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">Custom text</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addTextSnippet}>
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add text
                </Button>
              </div>
              {trainingDraft.textSnippets.length === 0 ? (
                <p className="text-xs text-slate-500">
                  Paste policies, service descriptions, or other text for your agent to reference.
                </p>
              ) : (
                trainingDraft.textSnippets.map((text, index) => (
                  <div key={`text-${index}`} className="space-y-1">
                    <Textarea
                      value={text}
                      rows={3}
                      placeholder="Enter training text…"
                      onChange={(e) => {
                        const next = [...trainingDraft.textSnippets];
                        next[index] = e.target.value;
                        updateDraft({ textSnippets: next });
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => {
                        const next = trainingDraft.textSnippets.filter((_, i) => i !== index);
                        updateDraft({ textSnippets: next });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm font-medium">Q&amp;A</Label>
                <div className="flex flex-wrap gap-2">
                  {onGenerateFaqs ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isGeneratingFaqs || !trainingDraft.companyProfileGenerated}
                      onClick={() => void onGenerateFaqs()}
                    >
                      {isGeneratingFaqs ? (
                        <>
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          Generating…
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-1 h-3.5 w-3.5" />
                          Generate FAQs
                        </>
                      )}
                    </Button>
                  ) : null}
                  <Button type="button" variant="ghost" size="sm" onClick={addQaPair}>
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add Q&amp;A
                  </Button>
                </div>
              </div>
              {trainingDraft.faqsGenerated ? (
                <p className="text-xs text-emerald-700">
                  FAQs generated from your website content.
                </p>
              ) : null}
              {trainingDraft.qaPairs.length === 0 ? (
                <p className="text-xs text-slate-500">
                  Generate FAQs from your website or add common questions manually.
                </p>
              ) : (
                trainingDraft.qaPairs.map((pair, index) => (
                  <div
                    key={`qa-${index}`}
                    className="space-y-2 rounded-md border border-slate-100 bg-slate-50/50 p-3"
                  >
                    <Input
                      value={pair.question}
                      placeholder="Question"
                      onChange={(e) => {
                        const next = [...trainingDraft.qaPairs];
                        next[index] = { ...pair, question: e.target.value };
                        updateDraft({ qaPairs: next });
                      }}
                    />
                    <Textarea
                      value={pair.answer}
                      rows={2}
                      placeholder="Answer"
                      onChange={(e) => {
                        const next = [...trainingDraft.qaPairs];
                        next[index] = { ...pair, answer: e.target.value };
                        updateDraft({ qaPairs: next });
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => {
                        const next = trainingDraft.qaPairs.filter((_, i) => i !== index);
                        updateDraft({ qaPairs: next });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { defaultHireWizardTrainingDraft };
